'use client';

import { SignIn, SignUp, useAuth, useClerk } from '@clerk/nextjs';
import { Inter } from 'next/font/google';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { Logo } from '@/code/frontend/components/ui/logo/logo';
import { trpc } from '@/code/lib/utils/trpc';

const inter = Inter({
  subsets: ['latin'],
  weight: ['400'], // Only including the regular (400) weight
});

export function HomePage({
  isEmployeeDeployment,
}: {
  isEmployeeDeployment: boolean;
}) {
  const searchParams = useSearchParams();
  const loggedIn = useAuth();
  const clerk = useClerk();
  const defaultRedirect = trpc.user.getDefaultRedirectUrl.useQuery(undefined, {
    enabled: !!loggedIn.isSignedIn,
    refetchOnWindowFocus: false,
  });

  const mode = searchParams?.get('mode');
  const requestedUrl = searchParams?.get('requestedUrl');

  const redirectUrl = requestedUrl ?? defaultRedirect?.data ?? '/';
  const signUpMode = !isEmployeeDeployment && mode !== 'sign-in';
  // An existing session would make <SignIn/> redirect away without redeeming
  // the ticket, so impersonating B while signed in as A would stay on A.
  const switchingAccounts =
    !!searchParams?.get('__clerk_ticket') && !!loggedIn.isSignedIn;

  useEffect(() => {
    if (switchingAccounts) {
      void clerk.signOut({ redirectUrl: window.location.href });
    }
  }, [clerk, switchingAccounts]);

  return (
    <div
      className={`${inter.className} flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4`}
    >
      <div className="flex justify-center mb-8">
        <Logo height={32} />
      </div>
      <h1 className="text-[40px] leading-tight mb-12 text-center font-normal">
        {isEmployeeDeployment
          ? 'Employee Sign In Page'
          : 'Welcome to the future of outbound'}
      </h1>
      {switchingAccounts ? null : signUpMode ? (
        <SignUp
          routing="hash"
          signInUrl={`/?mode=sign-in${
            requestedUrl ? `&requestedUrl=${requestedUrl}` : ''
          }`}
          signInForceRedirectUrl={redirectUrl}
          forceRedirectUrl={requestedUrl ? redirectUrl : undefined}
        />
      ) : (
        <SignIn
          routing="hash"
          signUpUrl={`/?mode=sign-up${
            requestedUrl ? `&requestedUrl=${requestedUrl}` : ''
          }`}
          signUpForceRedirectUrl={redirectUrl}
          forceRedirectUrl={requestedUrl ? redirectUrl : undefined}
        />
      )}
    </div>
  );
}
